// React
import { useContext, useMemo } from "react"

// i18n
import { useTrans, Trans } from "@/i18n"

// UI
import { Heading } from "@/components/Typography"
import { CardBody, CardWrapper } from "@/components/Card"
import { Tooltip } from "@/components/Tooltip"

// Icons
import { FiInfo, FiChevronRight } from "@/lib/icons"

// Forms
import { FormikProvider, useFormik } from "@/lib/formik"
import {
	FormikErrors,
	FormikInput,
	FormikSubmitButton,
	FormGroup,
	FormikLabel,
} from "@/components/form-controls"

// Queries
import { useBankDetailsQuery } from "@/api/graphql"
import { useApiV1InvestorIbanPartialUpdate } from "@/api/rest/generated/api/api"
import {
	ApiV1InvestorIbanPartialUpdate200,
	ApiV1InvestorIbanPartialUpdate400,
} from "@/api/rest/generated/@types"

// Context
import {
	ControlStatusEnum,
	useHiddenGetParamsContext,
} from "@/context/hiddenGetParams"
import { RedirectContext } from "@/context/redirectContext"
import { useToasts } from "@/context/toasts"

// Sentry
import { captureMessage } from "@/lib/sentry"

// Security
import { rewriteMangoPayReturnUrlToCurrentUrl } from "@/lib/security"

/**
 * BankDetails
 * @returns
 */
export const BankDetails = () => {
	// Translations
	const t = useTrans("profile")

	// Query
	const { data, isLoading } = useBankDetailsQuery()

	// Context
	const toasts = useToasts()
	const { redirect } = useContext(RedirectContext)
	const { controlStatus } = useHiddenGetParamsContext()

	// When control status is validated, show a success toast
	useMemo(() => {
		if (controlStatus === ControlStatusEnum.VALIDATED) {
			toasts.addToast({
				variant: "success",
				id: `personal-details-alert-${Date.now()}`,
				text: t("profile:profile.personal_details.success_message"),
			})
		}

		if (controlStatus === ControlStatusEnum.FAILED) {
			toasts.addToast({
				variant: "alert",
				id: `personal-details-alert-${Date.now()}`,
				text: t("profile:profile.personal_details.failed_message"),
			})
		}
	}, [controlStatus])

	// Mutation
	const updateIban = useApiV1InvestorIbanPartialUpdate({
		mutation: {
			onSuccess: async (response: ApiV1InvestorIbanPartialUpdate200) => {
				// do we have data? then success
				if (response?.verification_url) {
					redirect(
						rewriteMangoPayReturnUrlToCurrentUrl(
							response.verification_url,
						),
					)
				} else {
					// If no verification url, show a success toast
					toasts.addToast({
						variant: "success",
						id: `bank-details-alert-${Date.now()}`,
						text: t(
							"profile:profile.personal_details.success_message",
						),
					})
				}
			},
			onError: (response: ApiV1InvestorIbanPartialUpdate400) => {
				if (response) {
					toasts.addToast({
						variant: "alert",
						id: `bank-details-alert-${Date.now()}`,
						text: response.iban[0],
					})
					// Log errors to sentry
					captureMessage("Saving phone number failed", {
						level: "error",
						extra: {
							response,
						},
					})
				}
			},
		},
	})

	// Formik form
	const form = useFormik({
		enableReinitialize: true,
		initialValues: {
			full_name: data?.me?.full_name,
			iban: "",
		},
		onSubmit: async (values) => {
			await updateIban.mutateAsync({
				data: {
					iban: values.iban,
					// TODO: returnUrl: "",
				},
			})
		},
	})

	// Show loading state if data is loading
	if (isLoading) {
		return null
	}

	return (
		<CardWrapper>
			<FormikProvider value={form}>
				<form onSubmit={form.handleSubmit}>
					<CardBody>
						<Heading
							as="h2"
							styleAs="h5"
							className="mb-3 sm:truncate"
						>
							{t("profile:profile.bank_details.title")}
						</Heading>
						<FormGroup
							heading={
								<>
									<p className="whitespace-break-spaces">
										{t("profile:profile.bank_details.copy")}
										<br /> <br />
										{t(
											"profile:profile.bank_details.mangopay_copy",
										)}
									</p>

									{/** Logo */}
									<img
										src="/assets/partners/mangopay_logo.png"
										alt="Mangopay logo"
										className="w-1/2"
									/>

									{/** FAQ */}
									<p className="whitespace-break-spaces">
										<Trans
											i18nKey="investor:investor.identity.copy.mangopay.copy.faq"
											components={{
												/* eslint-disable jsx-a11y/anchor-has-content */
												a: (
													<a
														className="font-medium text-secondary-300 hover:text-secondary-700"
														href="https://informatie.zonhub.com/support/solutions/articles/17000147317-hoe-mangopay-als-payment-service-provider-zonhub-helpt"
														target="_blank"
														rel="noreferrer"
													/>
													/* eslint-enable jsx-a11y/anchor-has-content */
												),
											}}
										/>
									</p>
								</>
							}
						>
							<div
								className="w-fulls grid gap-6 2xl:grid-cols-2"
								data-testid="form-group"
							>
								{/** Name just as confirmation */}
								<div className="2xl:col-span-2">
									<FormikLabel
										htmlFor="full_name"
										className="flex"
									>
										<Tooltip
											content={
												<p>
													{t(
														"profile:profile.personal_details.name.tooltip",
													)}
												</p>
											}
										>
											<span className="flex items-center">
												{t(
													"profile:profile.personal_details.name.title",
												)}
												<FiInfo className="ml-2" />
											</span>
										</Tooltip>
									</FormikLabel>
									<FormikInput
										name="full_name"
										required
										className="mt-1 block w-full"
										readOnly
										disabled
										classNameWrapper="lg:w-1/2"
									/>
								</div>

								{/** Bank account number */}
								<div className="lg:w-1/2 2xl:col-span-2">
									<div className="flex flex-row gap-2">
										<div className="flex-1">
											<FormikLabel
												className="truncate"
												htmlFor="iban"
											>
												<span className="flex items-center">
													{t(
														"profile:profile.bank_details.account_number.title",
													)}
												</span>
											</FormikLabel>
											<FormikInput
												placeholder={
													data?.me?.investor
														?.masked_iban ??
													undefined
												}
												type="iban"
												name="iban"
												required
												data-testid="input-iban"
												id="iban"
												aria-label="iban"
												className="mt-1 block w-full"
											/>
										</div>

										<FormikSubmitButton
											className="self-end"
											disableWhenPristine
										>
											{t(
												"profile:profile.contact_details.confirm",
											)}
											<FiChevronRight className="ml-2" />
										</FormikSubmitButton>
									</div>

									{/** Errors */}
									<FormikErrors i18nNamespace="profile" />
								</div>
							</div>
						</FormGroup>
					</CardBody>
				</form>
			</FormikProvider>
		</CardWrapper>
	)
}
